import { useUpdateOffer } from '@services/offers/mutations/useUpdateOffer'
import {
  CalendarClock,
  Coins,
  Flame,
  Home as HomeIcon,
  Info,
  PiggyBank,
  Save,
  Wallet,
  Wifi,
  Zap,
  type LucideIcon
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OfferDetail, OfferWrite } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { MoneyInput } from '@/components/ui/money-input'

import {
  depositMaxAllowed,
  depositMonths,
  QUICK_FILL_MONTHS,
  round2
} from '../deposit'

const LOCKED = ['RESERVED', 'ONGOING', 'TERMINATED']

type FormState = {
  rent?: number
  deposit_amount?: number
  monthly_advance_prescription?: number
  electricity?: number
  gas?: number
  internet_tv?: number
  deadline?: string
}

const MONEY_FIELDS: {
  key: keyof FormState
  labelKey: string
  icon: LucideIcon
}[] = [
  { key: 'rent', labelKey: 'admin.edit_modal.rentAmount', icon: Wallet },
  {
    key: 'deposit_amount',
    labelKey: 'admin.edit_modal.depositAmount',
    icon: PiggyBank
  },
  {
    key: 'monthly_advance_prescription',
    labelKey: 'admin.edit_modal.monthlyAdvancePrescription',
    icon: HomeIcon
  },
  { key: 'electricity', labelKey: 'admin.edit_modal.electricity', icon: Zap },
  { key: 'gas', labelKey: 'admin.edit_modal.gas', icon: Flame },
  { key: 'internet_tv', labelKey: 'admin.edit_modal.internetTv', icon: Wifi }
]

function fromOffer(offer?: OfferDetail): FormState {
  return {
    rent: offer?.rent ?? undefined,
    deposit_amount: offer?.deposit_amount ?? undefined,
    monthly_advance_prescription:
      offer?.monthly_advance_prescription ?? undefined,
    electricity: offer?.electricity ?? undefined,
    gas: offer?.gas ?? undefined,
    internet_tv: offer?.internet_tv ?? undefined,
    deadline: offer?.deadline ? offer.deadline.slice(0, 10) : undefined
  }
}

function ReadonlyTile({
  icon: Icon,
  label,
  value
}: {
  icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <Card variant="light" className="p-4">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        {label}
      </div>
      <div className="mt-1 font-display text-xl font-bold text-ink">{value}</div>
    </Card>
  )
}

export function OfferDetailsCard({
  offer,
  id
}: {
  offer: OfferDetail | undefined
  id?: string
}) {
  const { t } = useTranslation('offers')
  const { mutate: updateOffer, isPending } = useUpdateOffer()
  const [form, setForm] = useState<FormState>(() => fromOffer(offer))

  useEffect(() => {
    setForm(fromOffer(offer))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, offer?.updated_at])

  const locked = !!offer?.status && LOCKED.includes(offer.status)

  // The deposit is an absolute amount in EUR, still capped at three times the
  // monthly costs - so the ceiling moves whenever the rent or utilities do.
  const monthlyBase =
    (Number(form.rent) || 0) +
    (Number(form.monthly_advance_prescription) || 0) +
    (Number(form.electricity) || 0) +
    (Number(form.gas) || 0) +
    (Number(form.internet_tv) || 0)
  const depositAmount = Number(form.deposit_amount) || 0
  const depositMax = depositMaxAllowed(monthlyBase)
  const depositOverCap = depositAmount > depositMax
  const months = depositMonths(depositAmount, monthlyBase)

  const set = (key: keyof FormState, value: FormState[keyof FormState]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleSave = () => {
    if (!id) return
    updateOffer({ id, body: { ...form } as OfferWrite })
  }

  return (
    <Card className="flex flex-col gap-5 p-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Coins className="h-5 w-5" />
        </span>
        <h3 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
          {t('admin.offerWidget.finances')}
        </h3>
      </div>

      {locked ? (
        <div className="grid grid-cols-2 gap-3">
          {MONEY_FIELDS.map((f) => (
            <ReadonlyTile
              key={f.key}
              icon={f.icon}
              label={t(f.labelKey)}
              value={
                `${form[f.key] ?? 0} €`
              }
            />
          ))}
          <ReadonlyTile
            icon={CalendarClock}
            label={t('admin.edit_modal.deadline')}
            value={
              form.deadline
                ? new Date(form.deadline).toLocaleDateString('sk-SK')
                : '—'
            }
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
            {MONEY_FIELDS.map((f) => {
              const isDeposit = f.key === 'deposit_amount'
              return (
                <div key={f.key}>
                  <Label className="mb-1.5 flex items-center gap-1.5">
                    <f.icon className="h-3.5 w-3.5 text-primary" />
                    {t(f.labelKey)}
                    {isDeposit && (
                      <span
                        title={t('admin.offerWidget.depositCap')}
                        className="inline-flex cursor-help text-ink/35 hover:text-primary"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </Label>
                  <MoneyInput
                    className={`h-10 ${
                      isDeposit && depositOverCap ? 'border-peach' : ''
                    }`}
                    value={form[f.key] as number | undefined}
                    onChange={(v) => set(f.key, v ?? undefined)}
                  />
                  {isDeposit && (
                    <>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        {QUICK_FILL_MONTHS.map((m) => (
                          <Button
                            key={m}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            disabled={!monthlyBase}
                            onClick={() =>
                              set('deposit_amount', round2(monthlyBase * m))
                            }
                          >
                            ×{m}
                          </Button>
                        ))}
                        <span className="text-xs text-muted-foreground">
                          {t('admin.form.depositOfMonthly', {
                            months: months.toFixed(1)
                          })}
                        </span>
                      </div>
                      {depositOverCap && (
                        <p className="mt-1.5 text-xs font-medium text-peach">
                          {t('admin.form.depositOverCap', { max: depositMax })}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )
            })}

            <div>
              <Label className="mb-1.5 flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-primary" />
                {t('admin.edit_modal.deadline')}
              </Label>
              <DatePicker
                value={form.deadline}
                onChange={(v) => set('deadline', v)}
              />
            </div>
          </div>

          <div className="mt-auto flex justify-end">
            <Button onClick={handleSave} disabled={isPending || depositOverCap}>
              <Save className="h-4 w-4" />
              {t('admin.offerWidget.editOffer')}
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
